import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";

import { useDemo } from "@/entities/demo";
import { RiskMeter, asRiskLevel, useRobot } from "@/entities/robot";
import { AmountPresets } from "@/features/amount-presets";
import { useStartDemoRobot } from "@/features/demo-actions";
import { classify } from "@/shared/api";
import { ROUTES } from "@/shared/config";
import { compareMoney, formatMoney } from "@/shared/lib";
import { AmountInput, Button, useToast } from "@/shared/ui";
import { ScreenSkeleton, ScreenState } from "@/widgets/screen-state";

/**
 * Запуск робота на демо-счёте (доработки §3.1: «указать виртуальную сумму,
 * запустить»).
 *
 * Один экран вместо трёх шагов реальной покупки. Второй шаг реального флоу
 * существует ради чекбокса о рисках и списания настоящих денег; ни того, ни
 * другого здесь нет, и подтверждение согласия на риск виртуальными средствами
 * было бы ритуалом без смысла.
 *
 * Минимальная сумма берётся у РОБОТА, а не смягчается для демо: песочница
 * обязана учить настоящим правилам, иначе человек, привыкший запускать за 50,
 * упрётся в отказ на реальном счёте.
 */
export function DemoStartPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const toast = useToast();
  const { id } = useParams();
  const robotId = Number(id);

  const robot = useRobot(robotId);
  const demo = useDemo("30d");
  const start = useStartDemoRobot(robotId);
  const [amount, setAmount] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string | null>(null);

  if (robot.isPending || demo.isPending) return <ScreenSkeleton blocks={[170, 150, 158]} />;

  if (robot.isError || demo.isError) {
    return (
      <ScreenState
        tone="loss"
        icon="warningFill"
        title={t("states.error.title")}
        body={t("states.error.body")}
        action={{
          label: t("states.error.action"),
          onClick: () => {
            void robot.refetch();
            void demo.refetch();
          },
        }}
      />
    );
  }

  const minAmount = robot.data.min_amount;
  const balance = demo.data.account.demo_balance;
  const value = amount ?? robot.data.recommended_amount ?? minAmount;
  const enough = compareMoney(balance, minAmount) >= 0;
  const limitReached =
    demo.data.limits.positions_used >= demo.data.limits.max_positions;

  const launch = () => {
    setFieldError(null);
    start.mutate(
      { amount: value },
      {
        onSuccess: () => {
          toast.show({
            tone: "profit",
            title: t("demo.start.done"),
            meta: formatMoney(value, { suffix: "USDT" }),
          });
          void navigate(ROUTES.home);
        },
        onError: (error) => {
          // Различие кодов приходит с бэкенда и означает разные экраны: то,
          // что чинится другой суммой, — подсказка у поля; состояние продукта
          // — тост. Клиент это различие не изобретает, а читает.
          const reaction = classify(error);
          if (reaction.kind === "inline") {
            setFieldError(reaction.message);
            return;
          }
          if (reaction.kind === "insufficient") {
            setFieldError(t("demo.start.notEnough"));
            return;
          }
          toast.show({
            tone: "loss",
            title:
              reaction.kind === "conflict" ? reaction.message : t("states.error.title"),
          });
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-3.5 px-5 py-3">
      <header className="flex flex-col gap-1">
        <h1 className="text-h2 font-bold text-text-primary">
          {t("demo.start.title", { name: robot.data.name })}
        </h1>
        <span className="text-caption font-normal text-text-secondary">
          {t("demo.start.subtitle")}
        </span>
      </header>

      <section className="flex flex-col gap-2 rounded-[22px] border border-border-subtle bg-background-elevated p-[18px]">
        <div className="flex items-baseline justify-between">
          <span className="text-label font-medium text-text-secondary">
            {t("buy.amountTitle")}
          </span>
          <span className="text-caption font-normal text-text-secondary">
            {t("demo.balance")} {formatMoney(balance, { suffix: "USDT" })}
          </span>
        </div>

        <AmountInput
          value={value}
          onChange={(next) => {
            setFieldError(null);
            setAmount(next);
          }}
          hint={t("buy.minAmount", {
            amount: formatMoney(minAmount, { suffix: "USDT" }),
          })}
          {...(fieldError === null ? {} : { error: fieldError })}
        />

        <AmountPresets balance={balance} min={minAmount} onPick={setAmount} />
      </section>

      <RiskMeter
        level={asRiskLevel(robot.data.risk_level)}
        maxDd={robot.data.declared_max_dd}
      />

      {limitReached ? (
        <ScreenState
          tone="info"
          icon="warningFill"
          title={t("demo.start.limit.title")}
          body={t("demo.start.limit.body", {
            count: demo.data.limits.max_positions,
          })}
        />
      ) : null}

      <Button
        size="cta"
        block
        loading={start.isPending}
        disabled={value === "" || !enough || limitReached || !demo.data.enabled}
        onClick={launch}
      >
        {t("demo.start.action")}
      </Button>
    </div>
  );
}
